import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    MessageCircle, Globe2, Calendar, Image as ImageIcon, BarChart3, Sparkles,
    Building2, Bell, CreditCard, Camera, Languages, ShieldCheck, ArrowRight,
} from "lucide-react";
import MarketingLayout, { useMLang } from "./MarketingLayout";

const FEAT = {
    en: {
        title: "Every feature your restaurant actually needs.",
        sub: "We obsess over the workflow of small-to-mid Latin restaurants. No card processing nonsense, no kitchen-display systems you'll never use. Just the stuff that sells food.",
        groups: [
            {
                heading: "Selling",
                items: [
                    { i: MessageCircle, t: "WhatsApp checkout", b: "Bypasses popup blockers on iOS/Android. Customers land in WhatsApp with the full order pre-formatted." },
                    { i: CreditCard, t: "Peer-to-peer payments", b: "Show your Lynk, Tpago, PayPal numbers — collect manually with zero processing fees." },
                    { i: Bell, t: "Notify-me for sold-out items", b: "Out of mangú? Customers can subscribe. When you mark it back in stock, they get an automatic WhatsApp/email ping." },
                ],
            },
            {
                heading: "Operations",
                items: [
                    { i: Calendar, t: "Order prep calendar", b: "See exactly how many sancochos to make on Saturday. One-click WhatsApp broadcast to that day's customers." },
                    { i: Camera, t: "AI menu photography", b: "Gemini Nano Banana generates professional plate photos in seconds. No food stylist required." },
                    { i: BarChart3, t: "Profitability dashboard", b: "Plug in your ingredient costs once. We compute margin, suggested price, and top-sellers for every dish." },
                ],
            },
            {
                heading: "Branding",
                items: [
                    { i: Sparkles, t: "100% white-label", b: "Your colors, your fonts, your hero text, your chef bio, your country flag. We disappear from the customer's view." },
                    { i: Languages, t: "Bilingual everywhere", b: "EN/ES toggle on the storefront, the admin, even the receipts. Your customers feel at home." },
                    { i: Globe2, t: "Country-aware decorations", b: "Dominican Republic, Mexico, Puerto Rico flags + cultural accents auto-applied based on your config." },
                ],
            },
            {
                heading: "Trust",
                items: [
                    { i: ShieldCheck, t: "Brute-force protection", b: "Admin login is rate-limited; passwords are bcrypt-hashed. We sweat the boring stuff so you don't." },
                    { i: Building2, t: "Daily backups", b: "Your menu, orders, and customers are backed up every 24 hours. Sleep easy." },
                ],
            },
        ],
        cta: "Start a 14-day free trial",
    },
    es: {
        title: "Todas las funciones que tu restaurante realmente necesita.",
        sub: "Nos obsesionamos con el flujo de trabajo de restaurantes latinos pequeños y medianos. Nada de procesadores de tarjetas absurdos ni pantallas de cocina que nunca usarás. Solo lo que vende comida.",
        groups: [
            {
                heading: "Ventas",
                items: [
                    { i: MessageCircle, t: "Pago por WhatsApp", b: "Evita los bloqueadores de pop-ups en iOS/Android. El cliente llega a WhatsApp con el pedido completo ya escrito." },
                    { i: CreditCard, t: "Pagos peer-to-peer", b: "Muestra tus números de Lynk, Tpago, PayPal — cobra manualmente sin comisiones." },
                    { i: Bell, t: "\"Avísame\" para platos agotados", b: "¿Sin mangú? Tus clientes se suscriben. Cuando lo marques disponible, reciben un ping automático por WhatsApp/correo." },
                ],
            },
            {
                heading: "Operaciones",
                items: [
                    { i: Calendar, t: "Calendario de prep", b: "Mira cuántos sancochos necesitas el sábado. Un click para enviar recordatorios masivos a los clientes del día." },
                    { i: Camera, t: "Fotos de menú con IA", b: "Gemini Nano Banana genera fotos profesionales de platos en segundos. Sin estilista de comida." },
                    { i: BarChart3, t: "Panel de rentabilidad", b: "Ingresa tus costos una vez. Calculamos margen, precio sugerido y los más vendidos." },
                ],
            },
            {
                heading: "Marca",
                items: [
                    { i: Sparkles, t: "100% marca blanca", b: "Tus colores, fuentes, texto principal, bio del chef, bandera de tu país. Desaparecemos de la vista del cliente." },
                    { i: Languages, t: "Bilingüe en todos lados", b: "Toggle EN/ES en la tienda, el admin y hasta los recibos. Tus clientes se sienten en casa." },
                    { i: Globe2, t: "Decoraciones según país", b: "Banderas de RD, México, Puerto Rico y acentos culturales se aplican automáticamente." },
                ],
            },
            {
                heading: "Confianza",
                items: [
                    { i: ShieldCheck, t: "Protección contra fuerza bruta", b: "El login de admin tiene rate limit; las contraseñas se hashean con bcrypt." },
                    { i: Building2, t: "Backups diarios", b: "Tu menú, pedidos y clientes se respaldan cada 24h. Duerme tranquilo." },
                ],
            },
        ],
        cta: "Empieza tu prueba gratis de 14 días",
    },
};

const MarketingFeatures = () => {
    const { lang } = useMLang();
    const c = FEAT[lang];
    return (
        <MarketingLayout testid="marketing-features">
            <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-16 pb-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <h1 className="font-serif font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">{c.title}</h1>
                    <p className="text-lg text-white/70 mt-6 max-w-2xl leading-relaxed">{c.sub}</p>
                </motion.div>
            </section>

            <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-12 space-y-14">
                {c.groups.map((g, gi) => (
                    <div key={gi} data-testid={`features-group-${gi}`}>
                        <h2 className="font-serif font-bold text-xl uppercase tracking-[0.18em] text-orange-300 mb-6">{g.heading}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {g.items.map((f, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className="p-6 rounded-2xl border border-white/10 bg-white/[0.03]"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-300 flex items-center justify-center mb-4">
                                        <f.i className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-serif font-bold text-lg mb-2">{f.t}</h3>
                                    <p className="text-sm text-white/60 leading-relaxed">{f.b}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
                <div className="rounded-3xl border border-orange-400/30 bg-orange-500/[0.06] p-8 sm:p-12 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
                    <h3 className="font-serif font-bold text-2xl sm:text-3xl max-w-xl">{c.cta}</h3>
                    <Link to="/heavenly/signup" data-testid="features-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-semibold whitespace-nowrap">
                        {c.cta}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
};

export default MarketingFeatures;
